import { activeWorkspaceTool } from './navigation'

const TAB_PREFIX = 'yuyuan-workbench-'
const BRIDGE_KEY = '__yuyuanWorkbenchTab'
const PENDING_URL_KEY = '__yuyuanWorkbenchPendingUrl'

export function workspaceTabName(route) {
  const tool = activeWorkspaceTool(route)
  if (tool) return TAB_PREFIX + tool
  if (route.name === 'VideoList') return TAB_PREFIX + 'projects'
  if (route.name === 'FeishuTables') return TAB_PREFIX + 'tables'
  if (route.name === 'InspirationMaterial') return TAB_PREFIX + 'inspiration'
  if (route.matched?.some(record => record.path === '/quick-script')) return TAB_PREFIX + 'quick-scripts'
  if (route.matched?.some(record => record.path === '/script-agent')) return TAB_PREFIX + 'scripts'
  return ''
}

// Reopening a tool should retain its current subpage, draft and scroll position.
// Only an explicit project change (or opening the project selector) navigates it.
export function workspaceTabDestination(current, requested) {
  if (workspaceTabName(current) !== workspaceTabName(requested)) return requested.fullPath
  if (requested.name === 'VideoList') return current.name === 'VideoList' ? null : requested.fullPath
  if (['VideoStudio', 'ImageGenerateStudio'].includes(requested.name)) {
    return current.name === requested.name && String(current.params.scriptId) === String(requested.params.scriptId)
      ? null : requested.fullPath
  }
  const scriptId = requested.query.scriptId
  if (scriptId && String(current.query.scriptId || '') !== String(scriptId)) {
    if (requested.name === 'CanvasList' || requested.name === 'SmartCanvas') {
      return { name: 'CanvasList', query: { scriptId } }
    }
    return { path: current.path, query: { ...current.query, scriptId }, hash: current.hash }
  }
  return null
}

export function registerWorkspaceTab(router, browser = window) {
  const bridge = {
    sessionToken: browser.localStorage.getItem('token'),
    async activate(location) {
      const destination = workspaceTabDestination(router.currentRoute.value, router.resolve(location))
      if (destination) await router.push(destination)
    }
  }
  browser[BRIDGE_KEY] = bridge
  const updateName = (to, from, failure) => {
    if (failure) return
    const name = workspaceTabName(to)
    if (name || browser.name.startsWith(TAB_PREFIX)) browser.name = name
  }
  updateName(router.currentRoute.value)
  const stop = router.afterEach(updateName)
  return () => {
    stop()
    if (browser[BRIDGE_KEY] !== bridge) return
    delete browser[BRIDGE_KEY]
    if (browser.name.startsWith(TAB_PREFIX)) browser.name = ''
  }
}

export async function openWorkspaceTab(router, location, browser = window) {
  const requested = router.resolve(location)
  const name = workspaceTabName(requested)
  const url = new URL(requested.href, browser.location.href)
  if (!name || url.origin !== browser.location.origin) throw new Error('Invalid workbench destination')

  // An empty URL obtains the named tab without loading its document again.
  // Keep this synchronous with the user's click to retain popup permission.
  const target = browser.open('', name)
  if (!target) return false
  target.focus()
  let currentUrl, bridge, pendingUrl
  try {
    currentUrl = target.location.href
    bridge = target[BRIDGE_KEY]
    pendingUrl = target[PENDING_URL_KEY]
  } catch (_) {
    // The named tab has left our origin. Restore its original app destination.
    target.location = url.href
    return true
  }
  if (bridge) {
    if (bridge.sessionToken === browser.localStorage.getItem('token')) {
      await bridge.activate(requested.fullPath)
    } else {
      // Never surface a retained view from a previous login session.
      target.location.replace(url.href)
    }
  } else if (currentUrl === 'about:blank') {
    // Repeated clicks while the first page is loading must not restart it.
    if (pendingUrl !== url.href) {
      target[PENDING_URL_KEY] = url.href
      target.location.replace(url.href)
    }
  } else {
    const current = new URL(currentUrl)
    const destination = workspaceTabDestination(router.resolve(current.pathname + current.search + current.hash), requested)
    if (destination) target.location.replace(router.resolve(destination).href)
  }
  return true
}
